import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { CampaignPreset } from 'src/api/campaignPresets/types';
import { useGetCampaignPresetApi } from 'src/api/campaignPresets';
import { DefaultCustomField, MessageTemplate } from 'src/constants/types';
import { Keyword } from 'src/modules/keywords/types';
import { GENERIC_ERROR_MESSAGE } from 'src/constants/messages';
import { useCampaignBuilderV2Api } from '../api';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { resolvePolicy } from '../policies';
import { generateUniqueCampaignName } from '../helpers/uniqueName';
import { Campaign, CampaignConfigSchema, ResolvedCampaignConfig } from '../types';

// Full UUID structure. A looser 36-character hex-or-hyphen test sends malformed
// deep links down the by-id branch, failing with the wrong request and the wrong
// error.
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Shown in the page body. What the API actually said goes to the console. */
export const CONFIG_UNAVAILABLE_MESSAGE = 'This campaign is not available.';
export const CONFIG_LOAD_FAILED_MESSAGE = GENERIC_ERROR_MESSAGE;

export interface CampaignConfigLoaderParams {
  configIdOrType: string;
  /** Limits a by-type lookup to one application's configs instead of the newest match across all. */
  applicationId?: string;
  campaignId?: string;
  duplicateCampaignId?: string;
  presetId?: string;
}

/**
 * Everything one builder open resolves. All of it is local state: the values are
 * read only inside the builder's own subtree, so BuilderContext carries them the
 * short distance they travel.
 */
export interface CampaignConfigLoaderResult {
  configId: string | null;
  config: ResolvedCampaignConfig | null;
  campaign: Campaign | null;
  isDuplicate: boolean;
  preset: CampaignPreset | null;
  /** `null` means not resolved yet — editors mounted before that strip merge tags. */
  metadata: DefaultCustomField[] | null;
  /** Loaded before editors mount; TipTap snapshots templates at construction. */
  messageTemplates: MessageTemplate[] | null;
  keywords: Keyword[];
  addKeyword: (keyword: Keyword) => void;
  /**
   * Name to seed the form with for the two flows that derive it from a source:
   * duplicate and start-from-preset. Resolved before the builder renders — a
   * later `form.reset` would clear the dirty flag and silently drop whatever the
   * user typed meanwhile. `null` means "use the policy's default name".
   */
  initialName: string | null;
  loading: boolean;
  error: string | null;
}

const INITIAL: CampaignConfigLoaderResult = {
  configId: null,
  config: null,
  campaign: null,
  isDuplicate: false,
  preset: null,
  metadata: null,
  messageTemplates: null,
  keywords: [],
  addKeyword: () => undefined,
  initialName: null,
  loading: true,
  error: null,
};

type Api = ReturnType<typeof useCampaignBuilderV2Api>;
type GetPreset = ReturnType<typeof useGetCampaignPresetApi>;

/** What the URL points at, before any dependent data is fetched. */
interface Target {
  config: ResolvedCampaignConfig | null;
  configId: string | null;
  campaign: Campaign | null;
  isDuplicate: boolean;
  preset: CampaignPreset | null;
}

const NO_TARGET: Target = {
  config: null, configId: null, campaign: null, isDuplicate: false, preset: null,
};

/** A known config id, from a preset or a campaign. */
async function configById(api: Api, id: string) {
  const res = await api.getCampaignConfig(id);
  return {
    config: res.data.config
      ? { ...res.data.config, applicationId: res.data.applicationId }
      : null,
    configId: res.data.id,
  };
}

/** What the route carries: either a config id or a campaign-type name. */
async function findConfig(api: Api, configIdOrType: string, applicationId?: string) {
  if (UUID_REGEX.test(configIdOrType)) return configById(api, configIdOrType);
  // getCampaignConfigByType returns CampaignConfigView | null (already unwrapped)
  const view = await api.getCampaignConfigByType(configIdOrType, applicationId);
  return {
    config: view ? { ...view.config, applicationId: view.applicationId } : null,
    configId: view?.id ?? null,
  };
}

/**
 * Which config, campaign and preset the URL means. Three entry points, checked
 * in order — a preset launch carries no configIdOrType at all, and a duplicate
 * derives its config from the source campaign.
 */
async function resolveTarget(
  api: Api,
  getCampaignPreset: GetPreset,
  companyId: string | undefined,
  { configIdOrType, campaignId, duplicateCampaignId, presetId, applicationId }: CampaignConfigLoaderParams,
): Promise<Target> {
  // Start from a preset: prefer its sourceConfigId, fall back to its campaign
  // type, since sourceConfigId is nullable.
  if (presetId && companyId) {
    const preset = (await getCampaignPreset(presetId, companyId)).data;
    const found = preset.sourceConfigId
      ? await configById(api, preset.sourceConfigId)
      : preset.type
        ? await findConfig(api, preset.type)
        : null;
    // A preset is a fresh draft, never an edit of its source.
    if (found?.config) return { ...NO_TARGET, ...found, preset };
    return { ...NO_TARGET, preset };
  }

  // Duplicate: the source campaign names the config.
  if (duplicateCampaignId && !campaignId && companyId) {
    const campaign = (await api.getCampaign(duplicateCampaignId, companyId)).data;
    const base = { ...NO_TARGET, campaign, isDuplicate: true };
    if (campaign.configId) return { ...base, ...(await configById(api, campaign.configId)) };
    // No configId on the source: fall through to the route's own config, keeping
    // the campaign we just loaded.
    if (!configIdOrType) return base;
    return { ...base, ...(await findConfig(api, configIdOrType, applicationId)) };
  }

  if (campaignId && companyId) {
    const campaign = (await api.getCampaign(campaignId, companyId)).data;
    const base = { ...NO_TARGET, campaign };
    if (campaign.configId) return { ...base, ...(await configById(api, campaign.configId)) };
    if (!configIdOrType) return base;
    return { ...base, ...(await findConfig(api, configIdOrType, applicationId)) };
  }

  // Nothing left to look up — getCampaignConfigByType('') would be meaningless.
  if (!configIdOrType) return NO_TARGET;

  return { ...NO_TARGET, ...(await findConfig(api, configIdOrType, applicationId)) };
}

export function useCampaignConfigLoader(
  params: CampaignConfigLoaderParams,
): CampaignConfigLoaderResult {
  const { configIdOrType, campaignId, duplicateCampaignId, presetId, applicationId } = params;
  const companyId = useSelector(getCurrentCompanyId);
  const api = useCampaignBuilderV2Api();
  const getCampaignPreset = useGetCampaignPresetApi();
  const [result, setResult] = useState<CampaignConfigLoaderResult>(INITIAL);
  // The load is several awaits deep, so navigating away mid-flight would
  // otherwise land a setState on an unmounted builder.
  //
  // The setup half matters as much as the cleanup: refs survive Fast Refresh and
  // StrictMode's simulated remount, so a cleanup-only effect would latch this
  // false on the first re-run and the builder would spin forever.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  const publish = useCallback((next: CampaignConfigLoaderResult) => {
    if (mounted.current) setResult(next);
  }, []);

  const load = useCallback(async () => {
    publish({ ...INITIAL, loading: true });
    const args = { configIdOrType, campaignId, duplicateCampaignId, presetId, applicationId };

    /** Merge-tag definitions. Never rejects: the builder renders without them. */
    const loadMetadata = async (config: CampaignConfigSchema) => {
      if (!companyId) return null;
      try {
        const res = await api.getTemplateDefaultValues({
          companyId,
          types: resolvePolicy(config).placeholderFamilies(),
        });
        return res.data.items ?? [];
      } catch (e) {
        console.error('Failed to load template default values', e);
        return [];
      }
    };

    /** Only when this campaign type shows the keyword picker. */
    const loadKeywords = async (config: CampaignConfigSchema, campaign?: Campaign | null) => {
      if (!companyId || !resolvePolicy(config).loadsKeywords(config)) return [];
      try {
        const res = await api.getKeywords({ companyId, limit: 1000, offset: 0 });
        const items = (res.data.items ?? []).filter(Boolean);
        const itemIds = new Set(items.map(keyword => keyword.id));
        // Keep associations that are archived, filtered, or outside the first
        // page. SelectWithSearch can only display and preserve selected ids that
        // also exist in its options array.
        const saved = (campaign?.keywords ?? [])
          .filter(keyword => keyword?.id && !itemIds.has(keyword.id));
        return [...saved, ...items];
      } catch (e) {
        console.error('Failed to load keywords', e);
        // The campaign already carries enough data to preserve its selections
        // even when the company keyword request is temporarily unavailable.
        return (campaign?.keywords ?? []).filter(Boolean);
      }
    };

    const loadMessageTemplates = async () => {
      if (!companyId) return [];
      try {
        const res = await api.getMessageTemplates({
          companyId, offset: 0, limit: 10000, status: 'active',
        });
        return (res.data.items ?? []).filter(template => template.companyId === companyId);
      } catch (e) {
        console.error('Failed to load message templates', e);
        return [];
      }
    };

    /**
     * Duplicates and presets inherit a name that has to be made unique. A
     * failure here must not stop the builder opening — the user can still edit
     * the name, and the API rejects a clash on save.
     */
    const loadInitialName = async (target: Target) => {
      const sourceName = target.isDuplicate ? target.campaign?.name : target.preset?.name;
      if (!sourceName || !companyId) return null;
      try {
        return await generateUniqueCampaignName(sourceName, companyId, (cid) =>
          api.listCampaigns(cid).then((r) => r.data));
      } catch (e) {
        console.error('Failed to generate a unique campaign name', e);
        return sourceName;
      }
    };

    try {
      const target = await resolveTarget(api, getCampaignPreset, companyId, args);

      // An empty lookup is a failed load, not an empty success: without an error
      // the builder would have no config, no error and nothing left to wait for.
      if (!target.config) {
        console.error(
          `No campaign config found for: ${presetId ? `preset ${presetId}` : configIdOrType || '(none)'}`,
        );
        publish({ ...INITIAL, ...target, error: CONFIG_UNAVAILABLE_MESSAGE, loading: false });
        return;
      }

      const [metadata, keywords, messageTemplates, initialName] = await Promise.all([
        loadMetadata(target.config),
        loadKeywords(target.config, target.campaign),
        loadMessageTemplates(),
        loadInitialName(target),
      ]);

      publish({
        ...INITIAL, ...target, metadata, keywords, messageTemplates, initialName, loading: false,
      });
    } catch (err: unknown) {
      console.error('Failed to load the campaign builder', err);
      publish({ ...INITIAL, error: CONFIG_LOAD_FAILED_MESSAGE, loading: false });
    }
  }, [publish, companyId, api, getCampaignPreset, configIdOrType, campaignId, duplicateCampaignId, presetId,
    applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const addKeyword = useCallback(
    (keyword: Keyword) => setResult(r => ({ ...r, keywords: [...r.keywords, keyword] })),
    [],
  );

  return useMemo(() => ({ ...result, addKeyword }), [result, addKeyword]);
}
